// TemplateManager.js

import React, { useState, useEffect, useRef } from 'react';
import api from '../../../services/api';
import toast from 'react-hot-toast';
import { 
  Image as ImageIcon, Video, Type, Trash2, RefreshCw, PlusCircle, 
  Globe, Store, Sparkles, UploadCloud, Search, MessageSquare, AlertCircle, 
  Edit3, X, ChevronLeft, ChevronRight, Link as LinkIcon, Film
} from 'lucide-react';

const TemplateManager = ({ estabelecimentoId }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState('todos'); // 'todos' | 'global' | 'loja'
  const [busca, setBusca] = useState('');
  
  // Paginação
  const [paginaAtual, setPaginaAtual] = useState(1);
  const itensPorPagina = 8;
  
  // Estados do Formulário
  const [templateId, setTemplateId] = useState(null);
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [tipoArquivo, setTipoArquivo] = useState('txt');
  const [file, setFile] = useState(null);
  const [urlVideo, setUrlVideo] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [videoModo, setVideoModo] = useState('upload'); // 'upload' | 'url'
  const [isTemplateGlobal, setIsTemplateGlobal] = useState(false);

  // Estados do Modal de Exclusão
  const [modalDeleteOpen, setModalDeleteOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fileImageRef = useRef(null);
  const fileVideoRef = useRef(null);
  const gridTopRef = useRef(null);

  const variaveis = [
    { tag: '@NomeContato', desc: 'Nome' },
    { tag: '@TelefoneContato', desc: 'Telefone' },
    { tag: '@v1', desc: 'Var 1' },
    { tag: '@v2', desc: 'Var 2' },
    { tag: '@v3', desc: 'Var 3' }
  ];

  useEffect(() => {
    buscarTemplates();
  }, [estabelecimentoId]);

  useEffect(() => {
    setPaginaAtual(1);
  }, [filtroTipo, busca]);

  const buscarTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/whatsapp/templates/${estabelecimentoId}`);
      setTemplates(res.data.payload || res.data || []);
    } catch (err) {
      toast.error("Erro ao carregar templates.");
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (caminho) => {
    if (!caminho) return '';
    if (caminho.startsWith('http://') || caminho.startsWith('https://')) {
      return caminho;
    }
    return `https://app.universomidiadigital.com.br${caminho.startsWith('/') ? '' : '/'}${caminho}`;
  };

  const checarSeGlobal = (t) => {
    const rawId = t.estabelecimentoId !== undefined ? t.estabelecimentoId : t.estabelecimento_id;
    if (t.is_global === true || t.is_global === 1 || t.isGlobal === true) return true;
    if (rawId === null || rawId === undefined || rawId === '' || rawId === 0 || rawId === '0') return true;
    return false;
  };

  const selecionarTemplate = (t) => {
    const global = checarSeGlobal(t);
    setTemplateId(t.id);
    setNome(t.NomeTemplate || '');
    setDescricao(t.Descricao || '');
    setTipoArquivo(t.TipoArquivo || 'txt');
    setUrlVideo(t.UrlVideo || '');
    setIsTemplateGlobal(global);
    
    if (t.CaminhoImagem && t.TipoArquivo === 'imagem') {
      setPreviewUrl(getImageUrl(t.CaminhoImagem));
    } else if (t.TipoArquivo === 'video') {
      if (t.CaminhoImagem) {
        setPreviewUrl(getImageUrl(t.CaminhoImagem));
        setVideoModo('upload');
      } else if (t.UrlVideo) {
        setPreviewUrl(t.UrlVideo);
        setVideoModo('url');
      } else {
        setPreviewUrl('');
      }
    } else {
      setPreviewUrl('');
    }
    setFile(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const limparFormulario = () => {
    setTemplateId(null);
    setNome('');
    setDescricao('');
    setTipoArquivo('txt');
    setFile(null);
    setPreviewUrl('');
    setUrlVideo('');
    setVideoModo('upload');
    setIsTemplateGlobal(false);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreviewUrl(URL.createObjectURL(selectedFile));
    }
  };

  const handleTipoChange = (novoTipo) => {
    setTipoArquivo(novoTipo);
    setFile(null);
    setPreviewUrl('');
    if (novoTipo === 'txt') {
      setUrlVideo('');
    } else if (novoTipo === 'imagem' && templateId) {
      const atual = templates.find(t => t.id === templateId);
      if (atual && atual.CaminhoImagem) {
        setPreviewUrl(getImageUrl(atual.CaminhoImagem));
      }
    } else if (novoTipo === 'video' && templateId) {
      const atual = templates.find(t => t.id === templateId);
      if (atual && atual.CaminhoImagem) {
        setPreviewUrl(getImageUrl(atual.CaminhoImagem));
        setVideoModo('upload');
      } else if (atual && atual.UrlVideo) {
        setPreviewUrl(atual.UrlVideo);
        setUrlVideo(atual.UrlVideo);
        setVideoModo('url');
      }
    }
  };

  const inserirVariavel = (tag) => {
    setDescricao(prev => prev ? `${prev} ${tag}` : tag);
    toast.success(`Tag ${tag} inserida!`, { duration: 1500 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nome.trim() || !descricao.trim()) {
      toast.error("Preencha o nome e a mensagem do template.");
      return;
    }

    const formData = new FormData();
    formData.append('nome', nome);
    formData.append('descricao', descricao);
    formData.append('tipo', tipoArquivo);
    formData.append('estabelecimentoId', estabelecimentoId);
    if (file) formData.append('file', file);
    if (tipoArquivo === 'video' && videoModo === 'url' && urlVideo) {
      formData.append('urlVideo', urlVideo);
    }

    try {
      if (templateId) {
        if (isTemplateGlobal) {
          await api.post(`/whatsapp/templates/${estabelecimentoId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success("Salvo como uma cópia exclusiva da sua loja!");
        } else {
          await api.put(`/whatsapp/templates/${estabelecimentoId}/${templateId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success("Template exclusivo atualizado!");
        }
      } else {
        await api.post(`/whatsapp/templates/${estabelecimentoId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success("Template exclusivo criado com sucesso!");
      }
      limparFormulario();
      buscarTemplates();
    } catch (err) {
      toast.error(err.response?.data?.error || "Erro ao processar template.");
    }
  };

  const abrirModalDeletar = (e, t) => {
    e.stopPropagation();
    if (checarSeGlobal(t)) {
      toast.error("Templates padrão do sistema não podem ser excluídos.");
      return;
    }
    setTemplateToDelete(t);
    setModalDeleteOpen(true);
  };

  const confirmarDeletar = async () => {
    if (!templateToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/whatsapp/templates/${estabelecimentoId}/${templateToDelete.id}`);
      toast.success("Template excluído com sucesso!");
      if (templateId === templateToDelete.id) {
        limparFormulario();
      }
      setModalDeleteOpen(false);
      setTemplateToDelete(null);
      buscarTemplates();
    } catch (err) {
      toast.error(err.response?.data?.error || "Erro ao excluir template.");
    } finally {
      setDeleting(false);
    }
  };

  // ==========================================================
  // LÓGICA DE ORGANIZAÇÃO E PAGINAÇÃO APRIMORADA
  // ==========================================================
  const templatesFiltrados = templates
    .filter(t => {
      const ehGlobal = checarSeGlobal(t);
      const matchFiltro = 
        filtroTipo === 'todos' ? true :
        filtroTipo === 'global' ? ehGlobal : !ehGlobal;

      const matchBusca = 
        (t.NomeTemplate || '').toLowerCase().includes(busca.toLowerCase()) ||
        (t.Descricao || '').toLowerCase().includes(busca.toLowerCase());

      return matchFiltro && matchBusca;
    })
    .sort((a, b) => {
      // 1º: Templates da Loja vem primeiro, Globais depois
      const aGlobal = checarSeGlobal(a);
      const bGlobal = checarSeGlobal(b);
      
      if (aGlobal === bGlobal) {
        // 2º: Ordem Alfabética dentro de cada grupo
        return (a.NomeTemplate || '').localeCompare(b.NomeTemplate || '');
      }
      return aGlobal ? 1 : -1;
    });

  const totalPaginas = Math.ceil(templatesFiltrados.length / itensPorPagina) || 1;
  
  // Trava de segurança: Se o usuário apagar o único template da última página, volta 1 página
  useEffect(() => {
    if (paginaAtual > totalPaginas && totalPaginas > 0) {
      setPaginaAtual(totalPaginas);
    }
  }, [totalPaginas, paginaAtual]);

  const indexInicio = (paginaAtual - 1) * itensPorPagina;
  const templatesPaginados = templatesFiltrados.slice(indexInicio, indexInicio + itensPorPagina);

  const mudarPagina = (novaPagina) => {
    setPaginaAtual(novaPagina);
    // Scroll suave para o topo do grid de templates
    setTimeout(() => {
      if (gridTopRef.current) {
        const offset = 80; // Dá um respiro pro cabeçalho não tampar o grid
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = gridTopRef.current.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    }, 50);
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1280px', margin: '0 auto', fontFamily: 'inherit', color: '#1e293b' }}>
      
      {/* Header iFood Style */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px', margin: 0 }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <MessageSquare size={22} color="#ea1d2c" />
            </div>
            Templates de Mensagens WhatsApp
          </h2>
          <p style={{ color: '#64748b', fontSize: '14px', margin: '4px 0 0 0' }}>
            Modelos automáticos para confirmação de pedidos, notificações de entrega e atendimento.
          </p>
        </div>

        <button 
          onClick={limparFormulario}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: '#ea1d2c',
            color: '#ffffff',
            border: 'none',
            padding: '11px 20px',
            borderRadius: '10px',
            fontWeight: '700',
            fontSize: '14px',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(234, 29, 44, 0.25)',
            transition: 'transform 0.15s ease'
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          <PlusCircle size={18} /> Novo Template
        </button>
      </div>

      {/* Grid Principal: Form + Preview */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '26px', marginBottom: '36px', alignItems: 'start' }}>
        
        {/* Editor Box */}
        <div style={{ background: '#ffffff', border: '1px solid #f1f5f9', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px' }}>
            <div>
              <h3 style={{ fontSize: '17px', fontWeight: '800', margin: 0, color: '#0f172a' }}>
                {templateId ? (isTemplateGlobal ? `Clonar & Personalizar: ${nome}` : `Editando: ${nome}`) : 'Criar Mensagem Automática'}
              </h3>
              <span style={{ fontSize: '12px', color: '#64748b', display: 'block', marginTop: '4px' }}>
                {isTemplateGlobal 
                  ? 'Este é um template padrão. Ao salvar, uma cópia exclusiva será criada para sua loja.' 
                  : 'Defina o texto, as tags inteligentes e anexe fotos ou vídeos.'}
              </span>
            </div>
            {isTemplateGlobal && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '4px 10px', background: '#eff6ff', color: '#2563eb', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>
                <Globe size={13} /> Padrão Sistema
              </span>
            )}
          </div>

          {/* Tags Variáveis */}
          <div style={{ marginBottom: '18px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '8px' }}>
              <Sparkles size={14} color="#ea1d2c" /> Clique para inserir tags dinâmicas:
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {variaveis.map(v => (
                <button
                  type="button"
                  key={v.tag}
                  onClick={() => inserirVariavel(v.tag)}
                  style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '700',
                    color: '#ea1d2c',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.15s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#ea1d2c'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
                >
                  <span>{v.tag}</span>
                  <small style={{ fontSize: '10px', color: '#94a3b8', fontWeight: '500' }}>({v.desc})</small>
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            
            {/* Nome do Template */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                Nome do Template *
              </label>
              <input 
                value={nome} 
                onChange={e => setNome(e.target.value)} 
                placeholder="Ex: Pedido Saiu para Entrega, Boas-vindas" 
                required 
                style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
              />
            </div>

            {/* Seletor de Formato */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                Formato da Mensagem
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                <button 
                  type="button" 
                  onClick={() => handleTipoChange('txt')} 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '11px',
                    borderRadius: '10px',
                    border: tipoArquivo === 'txt' ? '2px solid #ea1d2c' : '1px solid #e2e8f0',
                    background: tipoArquivo === 'txt' ? '#fef2f2' : '#ffffff',
                    color: tipoArquivo === 'txt' ? '#ea1d2c' : '#475569',
                    fontWeight: '700',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  <Type size={16}/> Apenas Texto
                </button>

                <button 
                  type="button" 
                  onClick={() => handleTipoChange('imagem')} 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '11px',
                    borderRadius: '10px',
                    border: tipoArquivo === 'imagem' ? '2px solid #ea1d2c' : '1px solid #e2e8f0',
                    background: tipoArquivo === 'imagem' ? '#fef2f2' : '#ffffff',
                    color: tipoArquivo === 'imagem' ? '#ea1d2c' : '#475569',
                    fontWeight: '700',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  <ImageIcon size={16}/> Imagem
                </button>

                <button 
                  type="button" 
                  onClick={() => handleTipoChange('video')} 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '11px',
                    borderRadius: '10px',
                    border: tipoArquivo === 'video' ? '2px solid #ea1d2c' : '1px solid #e2e8f0',
                    background: tipoArquivo === 'video' ? '#fef2f2' : '#ffffff',
                    color: tipoArquivo === 'video' ? '#ea1d2c' : '#475569',
                    fontWeight: '700',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  <Video size={16}/> Vídeo
                </button>
              </div>
            </div>

            {/* Módulo de Imagem */}
            {tipoArquivo === 'imagem' && (
              <div>
                <input 
                  type="file" 
                  ref={fileImageRef}
                  accept="image/*" 
                  onChange={handleFileChange} 
                  style={{ display: 'none' }} 
                />
                
                {previewUrl ? (
                  <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                    <img src={previewUrl} alt="Preview" style={{ width: '100%', maxHeight: '180px', objectFit: 'cover', display: 'block' }} />
                    <button 
                      type="button" 
                      onClick={() => fileImageRef.current?.click()} 
                      style={{ position: 'absolute', bottom: '10px', right: '10px', background: 'rgba(15, 23, 42, 0.85)', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                    >
                      Trocar Imagem
                    </button>
                  </div>
                ) : (
                  <div 
                    onClick={() => fileImageRef.current?.click()}
                    style={{ border: '2px dashed #cbd5e1', borderRadius: '12px', padding: '24px', textAlign: 'center', background: '#f8fafc', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}
                  >
                    <UploadCloud size={32} color="#ea1d2c" />
                    <strong style={{ fontSize: '13px', color: '#1e293b' }}>Clique para enviar uma imagem</strong>
                    <span style={{ fontSize: '11px', color: '#64748b' }}>PNG, JPG ou GIF até 10MB</span>
                  </div>
                )}
              </div>
            )}

            {/* Módulo de Vídeo com Opção de Upload ou Link */}
            {tipoArquivo === 'video' && (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px' }}>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                  <button
                    type="button"
                    onClick={() => { setVideoModo('upload'); setUrlVideo(''); }}
                    style={{
                      flex: 1,
                      padding: '8px',
                      borderRadius: '8px',
                      border: videoModo === 'upload' ? '1px solid #ea1d2c' : '1px solid #cbd5e1',
                      background: videoModo === 'upload' ? '#fef2f2' : '#ffffff',
                      color: videoModo === 'upload' ? '#ea1d2c' : '#475569',
                      fontWeight: '700',
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    <Film size={15} /> Upload de Arquivo MP4
                  </button>

                  <button
                    type="button"
                    onClick={() => { setVideoModo('url'); setFile(null); }}
                    style={{
                      flex: 1,
                      padding: '8px',
                      borderRadius: '8px',
                      border: videoModo === 'url' ? '1px solid #ea1d2c' : '1px solid #cbd5e1',
                      background: videoModo === 'url' ? '#fef2f2' : '#ffffff',
                      color: videoModo === 'url' ? '#ea1d2c' : '#475569',
                      fontWeight: '700',
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    <LinkIcon size={15} /> Link Externo (.mp4)
                  </button>
                </div>

                {videoModo === 'upload' ? (
                  <div>
                    <input 
                      type="file" 
                      ref={fileVideoRef}
                      accept="video/mp4,video/*" 
                      onChange={handleFileChange} 
                      style={{ display: 'none' }} 
                    />

                    {previewUrl ? (
                      <div style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', border: '1px solid #cbd5e1', background: '#000' }}>
                        <video src={previewUrl} controls style={{ width: '100%', maxHeight: '180px', display: 'block' }} />
                        <button 
                          type="button" 
                          onClick={() => fileVideoRef.current?.click()} 
                          style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(234, 29, 44, 0.9)', color: '#fff', border: 'none', padding: '5px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                        >
                          Trocar Vídeo
                        </button>
                      </div>
                    ) : (
                      <div 
                        onClick={() => fileVideoRef.current?.click()}
                        style={{ border: '2px dashed #cbd5e1', borderRadius: '10px', padding: '22px', textAlign: 'center', background: '#ffffff', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}
                      >
                        <Film size={28} color="#ea1d2c" />
                        <strong style={{ fontSize: '13px', color: '#1e293b' }}>Clique para enviar o arquivo de vídeo</strong>
                        <span style={{ fontSize: '11px', color: '#64748b' }}>Formato MP4 (Recomendado até 16MB)</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                      URL Direta do Vídeo MP4
                    </label>
                    <input 
                      type="url"
                      placeholder="https://seusite.com/videos/promocao.mp4" 
                      value={urlVideo} 
                      onChange={e => {
                        setUrlVideo(e.target.value);
                        setPreviewUrl(e.target.value);
                      }} 
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box' }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Mensagem WhatsApp */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                Corpo da Mensagem (WhatsApp Text) *
              </label>
              <textarea 
                value={descricao} 
                onChange={e => setDescricao(e.target.value)} 
                rows={6}
                placeholder="Olá @NomeContato, seu pedido está a caminho! 🛵"
                required
                style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical', outline: 'none' }}
              />
            </div>

            {/* Ações */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
              {templateId && (
                <button 
                  type="button" 
                  onClick={limparFormulario}
                  style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', color: '#475569', padding: '12px 18px', borderRadius: '10px', fontWeight: '700', cursor: 'pointer' }}
                >
                  Cancelar
                </button>
              )}
              <button 
                type="submit" 
                style={{ flex: 1, background: '#ea1d2c', color: '#ffffff', border: 'none', padding: '13px', borderRadius: '10px', fontWeight: '800', fontSize: '14px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(234, 29, 44, 0.25)' }}
              >
                {templateId ? (isTemplateGlobal ? 'Salvar Cópia da Loja' : 'Atualizar Template') : 'Salvar Novo Template'}
              </button>
            </div>

          </form>
        </div>

        {/* Simulador WhatsApp */}
        <div style={{ display: 'flex', justifyContent: 'center', position: 'sticky', top: '20px' }}>
          <div style={{ width: '100%', maxWidth: '330px', background: '#efeae2', borderRadius: '24px', overflow: 'hidden', border: '6px solid #1e293b', boxShadow: '0 16px 32px rgba(0,0,0,0.15)', boxSizing: 'border-box' }}>
            
            <div style={{ background: '#075e54', color: '#ffffff', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '34px', height: '34px', background: '#ffffff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                🍔
              </div>
              <div>
                <strong style={{ display: 'block', fontSize: '13px' }}>Atendimento Delivery</strong>
                <span style={{ fontSize: '10px', opacity: 0.85 }}>Online</span>
              </div>
            </div>

            <div style={{ padding: '16px', minHeight: '360px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', boxSizing: 'border-box' }}>
              
              <div style={{ 
                background: '#ffffff', 
                borderRadius: '8px 8px 8px 0px', 
                padding: '10px 12px', 
                maxWidth: '94%', 
                boxShadow: '0 1px 2px rgba(0,0,0,0.12)', 
                position: 'relative',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                whiteSpace: 'pre-wrap',
                boxSizing: 'border-box'
              }}>
                
                {tipoArquivo === 'imagem' && previewUrl && (
                  <img src={previewUrl} alt="Preview" style={{ width: '100%', maxHeight: '150px', objectFit: 'cover', borderRadius: '6px', marginBottom: '6px', display: 'block' }} />
                )}

                {tipoArquivo === 'video' && previewUrl && (
                  <video src={previewUrl} controls style={{ width: '100%', maxHeight: '150px', borderRadius: '6px', marginBottom: '6px', background: '#000' }} />
                )}

                {nome && (
                  <strong style={{ display: 'block', fontSize: '13px', marginBottom: '4px', color: '#0f172a', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {nome}
                  </strong>
                )}
                
                <p style={{ 
                  fontSize: '12px', 
                  color: '#334155', 
                  margin: 0, 
                  lineHeight: '1.45',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                  whiteSpace: 'pre-wrap'
                }}>
                  {descricao || 'Sua mensagem aparecerá aqui formatada exatamente como no WhatsApp do cliente...'}
                </p>

                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '3px', fontSize: '9px', color: '#94a3b8', marginTop: '6px' }}>
                  <span>14:30</span>
                  <span style={{ color: '#34b7f1' }}>✓✓</span>
                </div>
              </div>
            </div>

          </div>
        </div>

      </div>

      {/* Catálogo de Templates com Paginação e Identidade iFood */}
      <div ref={gridTopRef} style={{ borderTop: '1px solid #f1f5f9', paddingTop: '28px' }}>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: '800', margin: 0, color: '#0f172a' }}>
              Templates Disponíveis ({templatesFiltrados.length})
            </h3>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0 0' }}>
              Os templates com selo "Padrão Sistema" pertencem ao catálogo geral. Crie modelos personalizados para sua loja.
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            
            {/* Campo de Busca */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#ffffff', border: '1px solid #cbd5e1', padding: '7px 12px', borderRadius: '10px' }}>
              <Search size={16} color="#64748b" />
              <input 
                type="text" 
                placeholder="Buscar template..." 
                value={busca} 
                onChange={e => setBusca(e.target.value)} 
                style={{ border: 'none', outline: 'none', fontSize: '13px', background: 'transparent' }}
              />
            </div>

            {/* Abas de Filtros */}
            <div style={{ display: 'flex', background: '#f8fafc', padding: '3px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <button 
                onClick={() => setFiltroTipo('todos')}
                style={{
                  border: 'none',
                  background: filtroTipo === 'todos' ? '#ffffff' : 'transparent',
                  color: filtroTipo === 'todos' ? '#ea1d2c' : '#64748b',
                  padding: '6px 14px',
                  fontSize: '12px',
                  fontWeight: '700',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  boxShadow: filtroTipo === 'todos' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'
                }}
              >
                Todos ({templates.length})
              </button>

              <button 
                onClick={() => setFiltroTipo('global')}
                style={{
                  border: 'none',
                  background: filtroTipo === 'global' ? '#ffffff' : 'transparent',
                  color: filtroTipo === 'global' ? '#2563eb' : '#64748b',
                  padding: '6px 14px',
                  fontSize: '12px',
                  fontWeight: '700',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  boxShadow: filtroTipo === 'global' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'
                }}
              >
                <Globe size={13} /> Padrão Sistema ({templates.filter(t => checarSeGlobal(t)).length})
              </button>

              <button 
                onClick={() => setFiltroTipo('loja')}
                style={{
                  border: 'none',
                  background: filtroTipo === 'loja' ? '#ffffff' : 'transparent',
                  color: filtroTipo === 'loja' ? '#ea1d2c' : '#64748b',
                  padding: '6px 14px',
                  fontSize: '12px',
                  fontWeight: '700',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  boxShadow: filtroTipo === 'loja' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'
                }}
              >
                <Store size={13} /> Da Minha Loja ({templates.filter(t => !checarSeGlobal(t)).length})
              </button>
            </div>

            <button 
              onClick={buscarTemplates} 
              style={{ background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '10px', padding: '8px', cursor: 'pointer', color: '#475569' }}
              title="Atualizar lista"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Grade de Cards */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', background: '#f8fafc', borderRadius: '16px', color: '#64748b' }}>
            <RefreshCw size={28} className="animate-spin" style={{ margin: '0 auto 8px auto', color: '#ea1d2c' }} />
            <p style={{ margin: 0 }}>Carregando templates...</p>
          </div>
        ) : templatesFiltrados.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', background: '#f8fafc', borderRadius: '16px', color: '#64748b' }}>
            <AlertCircle size={36} color="#94a3b8" style={{ margin: '0 auto 8px auto' }} />
            <h4 style={{ margin: '0 0 4px 0', color: '#1e293b' }}>Nenhum template encontrado</h4>
            <p style={{ margin: 0, fontSize: '13px' }}>Ajuste os filtros ou crie um novo modelo para sua loja.</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '18px' }}>
              {templatesPaginados.map(t => {
                const ehGlobal = checarSeGlobal(t);
                const isSelected = templateId === t.id;

                return (
                  <div 
                    key={t.id} 
                    onClick={() => selecionarTemplate(t)}
                    style={{
                      background: '#ffffff',
                      borderRadius: '14px',
                      border: isSelected ? '2px solid #ea1d2c' : '1px solid #f1f5f9',
                      borderTop: ehGlobal ? '4px solid #3b82f6' : '4px solid #ea1d2c',
                      padding: '16px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      boxShadow: isSelected ? '0 0 0 2px rgba(234, 29, 44, 0.15)' : '0 2px 10px rgba(0,0,0,0.03)',
                      transition: 'all 0.2s ease',
                      position: 'relative'
                    }}
                  >
                    <div>
                      {/* Top Header do Card: Tag e Lixeira */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        {ehGlobal ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: '700', padding: '3px 8px', borderRadius: '12px', background: '#eff6ff', color: '#1d4ed8' }}>
                            <Globe size={11} /> Padrão Sistema
                          </span>
                        ) : (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: '700', padding: '3px 8px', borderRadius: '12px', background: '#fef2f2', color: '#ea1d2c' }}>
                            <Store size={11} /> Exclusivo Loja
                          </span>
                        )}

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>
                            {t.TipoArquivo === 'imagem' ? '🖼️ Imagem' : t.TipoArquivo === 'video' ? '🎥 Vídeo' : '📝 Texto'}
                          </span>
                          
                          {/* Ícone da Lixeira para templates da loja */}
                          {!ehGlobal && (
                            <button 
                              type="button"
                              onClick={(e) => abrirModalDeletar(e, t)} 
                              style={{ 
                                background: '#fee2e2', 
                                border: 'none', 
                                color: '#ef4444', 
                                cursor: 'pointer', 
                                padding: '5px 7px',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              title="Excluir Template da Loja"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Prévia de Imagem */}
                      {t.CaminhoImagem && t.TipoArquivo === 'imagem' && (
                        <img src={getImageUrl(t.CaminhoImagem)} alt="Thumb" style={{ width: '100%', height: '90px', objectFit: 'cover', borderRadius: '8px', marginBottom: '10px', display: 'block' }} />
                      )}

                      {/* Prévia de Vídeo */}
                      {t.TipoArquivo === 'video' && (t.CaminhoImagem || t.UrlVideo) && (
                        <div style={{ width: '100%', height: '90px', background: '#0f172a', borderRadius: '8px', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                          <Film size={28} color="#ea1d2c" />
                        </div>
                      )}

                      <h4 style={{ fontSize: '14px', fontWeight: '800', margin: '0 0 6px 0', color: '#0f172a', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {t.NomeTemplate}
                      </h4>

                      <p style={{ fontSize: '12px', color: '#64748b', margin: 0, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: '1.45', wordBreak: 'break-word' }}>
                        {t.Descricao || 'Sem descrição cadastrada...'}
                      </p>
                    </div>

                    <div style={{ marginTop: '14px', borderTop: '1px solid #f1f5f9', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '11px', fontWeight: '800', color: '#ea1d2c', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Edit3 size={13} /> {ehGlobal ? 'Clonar / Personalizar' : 'Editar Template'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Barra de Paginação iFood */}
            {totalPaginas > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '28px' }}>
                <button
                  type="button"
                  disabled={paginaAtual === 1}
                  onClick={() => mudarPagina(Math.max(paginaAtual - 1, 1))}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '8px 16px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    background: paginaAtual === 1 ? '#f8fafc' : '#ffffff',
                    color: paginaAtual === 1 ? '#94a3b8' : '#1e293b',
                    cursor: paginaAtual === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: '700'
                  }}
                >
                  <ChevronLeft size={16} /> Anterior
                </button>

                <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '700' }}>
                  Página <span style={{ color: '#ea1d2c' }}>{paginaAtual}</span> de <span>{totalPaginas}</span>
                </div>

                <button
                  type="button"
                  disabled={paginaAtual === totalPaginas}
                  onClick={() => mudarPagina(Math.min(paginaAtual + 1, totalPaginas))}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '8px 16px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    background: paginaAtual === totalPaginas ? '#f8fafc' : '#ffffff',
                    color: paginaAtual === totalPaginas ? '#94a3b8' : '#1e293b',
                    cursor: paginaAtual === totalPaginas ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: '700'
                  }}
                >
                  Próxima <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}

      </div>

      {/* Modal de Confirmação de Exclusão */}
      {modalDeleteOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(15, 23, 42, 0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          padding: '20px',
          boxSizing: 'border-box'
        }}>
          <div style={{
            background: '#ffffff',
            width: '100%',
            maxWidth: '420px',
            borderRadius: '20px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            border: '1px solid #e2e8f0',
            animation: 'fadeInModal 0.2s ease-out'
          }}>
            <div style={{
              background: '#ea1d2c',
              padding: '22px',
              color: '#ffffff',
              textAlign: 'center',
              position: 'relative'
            }}>
              <button 
                onClick={() => setModalDeleteOpen(false)}
                style={{
                  position: 'absolute',
                  top: '14px',
                  right: '14px',
                  background: 'transparent',
                  border: 'none',
                  color: '#ffffff',
                  cursor: 'pointer'
                }}
              >
                <X size={20} />
              </button>

              <div style={{
                width: '50px',
                height: '50px',
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 10px auto'
              }}>
                <Trash2 size={26} color="#ffffff" />
              </div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800' }}>Excluir Template</h3>
            </div>

            <div style={{ padding: '24px 20px', textAlign: 'center', color: '#334155' }}>
              <p style={{ margin: '0 0 10px 0', fontSize: '14px', lineHeight: '1.5' }}>
                Tem certeza que deseja excluir este template da sua loja?
              </p>
              <strong style={{ fontSize: '15px', color: '#0f172a', display: 'block', marginBottom: '10px' }}>
                "{templateToDelete?.NomeTemplate}"
              </strong>
              <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                Esta ação não poderá ser desfeita.
              </p>
            </div>

            <div style={{ padding: '16px 20px', background: '#f8fafc', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setModalDeleteOpen(false)}
                disabled={deleting}
                style={{
                  flex: 1,
                  background: '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: '10px',
                  padding: '11px',
                  fontSize: '13px',
                  fontWeight: '700',
                  color: '#475569',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={confirmarDeletar}
                disabled={deleting}
                style={{
                  flex: 1,
                  background: '#ea1d2c',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '11px',
                  fontSize: '13px',
                  fontWeight: '800',
                  color: '#ffffff',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  opacity: deleting ? 0.7 : 1
                }}
              >
                {deleting ? 'Excluindo...' : 'Sim, Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default TemplateManager;